import { ProvisionType } from '@wix/ambassador-funnel-intro-v1-meta-site-provision-descriptor/types'
import { Box, Button, Input, Loader } from '@wix/design-system'
import { useContent, useExperiments, useMetadata, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { retry, tryCatch } from '@wix/funnel-utils'
import React, { FC, useEffect, useState } from 'react'

import { provisionMetasite } from '../../../api/FunnelIntroSiteProvisioningService/provisionMetasite'
import { suggestDomains } from '../../../api/suggestDomains'
import { usePageLogger } from '../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../category'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { TWixMobileBusinessName as Content } from './WixMobileBusinessName.content'
import { wixMobileBusinessNameDataHooks as dataHooks } from './WixMobileBusinessName.dataHooks'

export const WixMobileBusinessName: FC = () => {
  const { content } = useContent<Content>()
  const { save, appDefIds = [], businessName: previousBusinessName, category } = useStorageContext()
  const [businessName, setBusinessName] = useState(previousBusinessName || '')
  const [pageLoading] = usePageLoading()
  const experiments = useExperiments()
  const pageLogger = usePageLogger()
  const next = useNext()
  const [isSaveSiteDetailsInProgress, setIsSaveSiteDetailsInProgress] = useState(false)
  const [{ biSlug: flowBiSlug }] = useMetadata()

  const provisionMetaSite = async () => {
    const { industryId, structureId, displayName, postRegVertical, businessTermId, businessSegmentId } =
      resolveCategoryFromBusinessTerm(category?.businessTerm, category?.businessSegmentId)

    const response = await provisionMetasite({
      type: ProvisionType.DASHBOARD_FIRST,
      origin: flowBiSlug,
      metadata: {
        flow: flowBiSlug,
      },
      additionalIntroData: {
        businessName,
        industryId,
        structureId,
        category: {
          industryId,
          structureId,
          displayName,
          postRegVertical,
          businessTermId,
          businessSegmentId,
        },
        selectedApps: appDefIds.map(appDefIds => ({ appDefId: appDefIds })),
      },
    })

    const metaSiteId = response.data.metaSiteProvisionDescriptor?.metaSiteId || undefined

    save({ provisionedMetaSiteId: metaSiteId })

    return metaSiteId
  }

  const suggestDomain = async () => {
    if (!businessName) {
      return
    }

    const response = await suggestDomains({ query: businessName, limit: 1 })
    const suggestion = response.data.suggestions?.[0]
    save({ suggestedDomain: suggestion?.domain || '' })
  }

  const onBackButtonClick = () => {
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }

  const onNextButtonClick = async () => {
    if (pageLoading || isSaveSiteDetailsInProgress) {
      return
    }

    setIsSaveSiteDetailsInProgress(true)

    save({ businessName })

    const provisionMetasitePromise = tryCatch(() => retry(() => provisionMetaSite()))
    const suggestedDomainPromise = tryCatch(() => retry(() => suggestDomain()))
    const [[error, metaSiteId]] = await Promise.all([provisionMetasitePromise, suggestedDomainPromise])

    if (!error) {
      if (!businessName) {
        await pageLogger.skip()
        next({ [content.question.questionKey]: { skipped: true } }, { metaSiteId })
        return
      }

      void pageLogger.answer({ answer: businessName })
      next({ [content.question.questionKey]: { values: [businessName], skipped: !businessName } }, { metaSiteId })
      return
    }

    setIsSaveSiteDetailsInProgress(false)
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  return (
    <WixMobilePage>
      <WixMobileHeader leftContent={<WixMobileBackButton onBack={onBackButtonClick} dataHook={dataHooks.back()} />} />
      <WixMobileContentWrapper>
        <WixMobileStepProgressBar
          current={content.stepProgressBar.content.current}
          total={content.stepProgressBar.content.total}
          dataHook={dataHooks.stepProgressBar()}
        />
        <WixMobileHeading dataHook={dataHooks.title()}>{content.question.content.title}</WixMobileHeading>
        <WixMobileDescription dataHook={dataHooks.subtitle()}>
          <span id="description">{content.question.content.subtitle}</span>
        </WixMobileDescription>
        <Input
          value={businessName}
          onChange={e => setBusinessName(e.target.value)}
          placeholder={content.question.content.placeholder}
          dataHook={dataHooks.input()}
          clearButton
          onClear={() => setBusinessName('')}
          size="large"
        />
      </WixMobileContentWrapper>
      <WixMobileFooter>
        {content.primaryBtn && (
          <Button size="large" fullWidth onClick={onNextButtonClick} dataHook={dataHooks.primaryBtn()}>
            <Box gap="SP1" verticalAlign="middle">
              {isSaveSiteDetailsInProgress ? <Loader size="tiny" /> : content.primaryBtn}
            </Box>
          </Button>
        )}
      </WixMobileFooter>
    </WixMobilePage>
  )
}
